/**
 * SageProductSelector - Reusable SAGE product autocomplete component
 * 
 * Used in:
 * - Config. Lojas → Mapeamentos SKU
 * - Artigos por Resolver → Mapear para SAGE
 * - Stocks por Resolver → Corrigir artigo
 * 
 * Features:
 * - Autocomplete search against /api/products
 * - Filters out SERVICE products (only physical products)
 * - Requires explicit selection (free text not allowed)
 * - Shows product details after selection
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Search, Loader2, CheckCircle, Package, AlertCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SageProductSelector({
  label = "Artigo SAGE",
  placeholder = "Pesquisar artigo no catálogo...",
  value,           // Selected product object or null
  onChange,        // (product: object | null) => void
  disabled = false,
  error = null,
  helperText = "Pesquise pelo código ou descrição. Apenas artigos físicos são mostrados.",
  showStockInfo = false,
  warehouse = null, // For stock lookup
  'data-testid': dataTestId = "sage-product-selector",
}) {
  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [stockInfo, setStockInfo] = useState(null);
  const [loadingStock, setLoadingStock] = useState(false);

  const searchSeq = useRef(0);

  // Search products with debounce (stale responses are discarded)
  const searchProducts = useCallback(async (query) => {
    const seq = ++searchSeq.current;
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setSearching(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/products`, {
        params: { search: query, limit: 15 },
        headers: { Authorization: `Bearer ${token}` },
      });
      
      // Filter out SERVICE products - only allow physical products
      const products = res.data.items || res.data.products || [];
      const physicalProducts = products.filter(p => !p.is_service);
      if (seq !== searchSeq.current) return;
      setSearchResults(physicalProducts);
    } catch (err) {
      console.error('Error searching products:', err);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchText && !value) {
        searchProducts(searchText);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchText, value, searchProducts]);

  // Load stock info when product selected and warehouse provided
  useEffect(() => {
    if (showStockInfo && value && warehouse) {
      loadStockInfo(value.sku, warehouse);
    } else {
      setStockInfo(null);
    }
  }, [value, warehouse, showStockInfo]);

  const loadStockInfo = async (sku, wh) => {
    setLoadingStock(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/stock-issues/live-stock`, {
        params: { sku, warehouse: wh },
        headers: { Authorization: `Bearer ${token}` },
      });
      setStockInfo({ available: res.data.available, warehouse: wh, source: res.data.source });
    } catch (err) {
      console.error('Error loading stock:', err);
      setStockInfo({ available: null, warehouse: wh, error: true, source: 'error' });
    } finally {
      setLoadingStock(false);
    }
  };

  // Handle product selection
  const handleSelect = (product) => {
    onChange(product);
    setSearchText('');
    setSearchResults([]);
  };

  // Handle text change - clear selection if user types after selecting
  const handleTextChange = (e) => {
    const newText = e.target.value;
    setSearchText(newText);
    
    // Clear selection if user modifies text
    if (value) {
      onChange(null);
    }
  };

  // Clear selection
  const handleClear = () => {
    onChange(null);
    setSearchText('');
    setSearchResults([]);
  };

  return (
    <div className="space-y-2" data-testid={dataTestId}>
      {label && <Label>{label}</Label>}
      
      <div className="relative">
        {/* Search Input */}
        {!value ? (
          <>
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
              <Input
                placeholder={placeholder}
                value={searchText}
                onChange={handleTextChange}
                disabled={disabled}
                className={`pl-9 font-mono ${error ? 'border-red-500' : ''}`}
                data-testid={`${dataTestId}-input`}
              />
              {searching && (
                <Loader2 className="absolute right-3 top-2.5 h-4 w-4 animate-spin text-slate-400" />
              )}
            </div>
            
            {/* Autocomplete dropdown */}
            {searchResults.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-64 overflow-y-auto">
                {searchResults.map((product) => (
                  <button
                    key={product.sku}
                    type="button"
                    className="w-full px-3 py-2 text-left hover:bg-slate-50 flex items-center justify-between border-b last:border-b-0"
                    onClick={() => handleSelect(product)}
                    data-testid={`${dataTestId}-option-${product.sku}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-mono font-medium text-sm">{product.sku}</div>
                      <div className="text-xs text-slate-600 truncate">
                        {product.name}
                      </div>
                    </div>
                    <div className="ml-2 flex-shrink-0">
                      <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">
                        <Package className="w-3 h-3 inline mr-1" />
                        Físico
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
            
            {/* No results message */}
            {searchText.length >= 2 && !searching && searchResults.length === 0 && (
              <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg p-3 text-sm text-slate-500">
                <AlertCircle className="w-4 h-4 inline mr-2" />
                Nenhum artigo encontrado para "{searchText}"
              </div>
            )}
          </>
        ) : (
          /* Selected Product Display */
          <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
            <div className="flex items-start justify-between">
              <div>
                <div className="font-medium text-emerald-900">{value.name}</div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="font-mono text-sm text-emerald-700">{value.sku}</span>
                  <CheckCircle className="w-4 h-4 text-emerald-600" />
                  <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">
                    Produto Físico
                  </span>
                </div>
                
                {/* Stock info */}
                {showStockInfo && stockInfo && (
                  <div className="mt-2 text-sm">
                    {loadingStock ? (
                      <span className="text-slate-500">
                        <Loader2 className="w-3 h-3 inline animate-spin mr-1" />
                        A verificar stock...
                      </span>
                    ) : stockInfo.available !== null ? (
                      <span className={stockInfo.available > 0 ? 'text-emerald-700' : 'text-amber-700'}>
                        Stock armazém {stockInfo.warehouse}: <strong>{stockInfo.available}</strong> un.
                        <span className="ml-1 text-xs text-slate-400">
                          ({stockInfo.source === 'sage_live' ? 'SAGE em tempo real' : 'cache local'})
                        </span>
                      </span>
                    ) : (
                      <span className="text-slate-500">Stock não disponível</span>
                    )}
                  </div>
                )}
              </div>
              
              <button
                type="button"
                onClick={handleClear}
                disabled={disabled}
                className="text-slate-400 hover:text-slate-600 p-1"
                title="Limpar seleção"
              >
                ×
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* Helper text */}
      {!value && helperText && (
        <p className="text-xs text-slate-500">{helperText}</p>
      )}
      
      {/* Error message */}
      {error && (
        <p className="text-xs text-red-600">{error}</p>
      )}
    </div>
  );
}
